import React, { useState } from 'react';
import LoadingData from 'components/loading-data';
import Link from 'components/link';
import Button from 'components/control/button';
import { getDistance } from 'geolib';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import CheckmarkCircleIcon from 'components/icon/checkmark-circle-icon';
import CloseCircleIcon from 'components/icon/close-circle-icon';
import { listingPageModalIds } from 'constants/test-constants';
import School, { getNearbySchools } from 'data/schools';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { Point } from 'data/listing';
import type { TFunction } from 'i18next';

//#region Types

const SCHOOL_LEVEL_ELEMENTARY = 'elementary';
const SCHOOL_LEVEL_SECONDARY = 'secondary';
type SchoolLevel = typeof SCHOOL_LEVEL_ELEMENTARY | typeof SCHOOL_LEVEL_SECONDARY;

type NearbySchoolsProps = {
  /** The geo location of the listing */
  position: Point;
  /** The schools to display */
  schools?: readonly Record<string, unknown>[];
  /** Whether the schools have been fetched */
  hasFetched?: boolean;
  /** Set the hasFetched state */
  setHasFetched?: (hasFetched: boolean) => void;
  /** Set the hasSchools state */
  setHasSchools?: (hasSchools: boolean) => void;
  /** Event handler for when a school link is clicked */
  onSchoolLinkClick?: () => void;
};
//#endregion

//#region Components

/** Component to display nearby schools for a given listing geo location */
function Schools({ schools, position, onSchoolLinkClick }: { schools: School[]; position: Point; onSchoolLinkClick?: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const [schoolLevelSelected, setSchoolLevelSelected] = useState<SchoolLevel>(SCHOOL_LEVEL_ELEMENTARY);

  return (
    <div className={styles.component}>
      <div className={styles.tabs}>
        <Button label={t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_ELEMENTARY)} className={buildClassName(styles.tab, getButtonClass(schoolLevelSelected, SCHOOL_LEVEL_ELEMENTARY)/*, isZoocasaTheme && styles['zoocasa-theme']*/)} onClick={() => setSchoolLevelSelected(SCHOOL_LEVEL_ELEMENTARY)} testId={listingPageModalIds.elementarySchoolsButton} />
        <Button label={t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_SECONDARY)} className={buildClassName(styles.tab, getButtonClass(schoolLevelSelected, SCHOOL_LEVEL_SECONDARY)/*, isZoocasaTheme && styles['zoocasa-theme']*/)} onClick={() => setSchoolLevelSelected(SCHOOL_LEVEL_SECONDARY)} testId={listingPageModalIds.secondarySchoolsButton} />
      </div>

      <p className={styles['description']}>
        {t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_DESCRIPTION)}
        {filterSchoolsByLevel(filterSchoolsByBoundary(schools, position), schoolLevelSelected).map((school, index, arr) => (
          <span key={index}>
            <Link href={`/school/${school.slug}`} className={styles['likely-attend-school']} onClick={onSchoolLinkClick}>
              {''} {school.name}
            </Link>
            {getSchoolNameListSeparator(arr.length, index, t)}
          </span>
        ))}
        <br /><br />
        {t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_CONFIRMATION)}
      </p>

      <div className={styles['title-row']}>
        <span className={styles['name-column']}>{t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_NAME)}</span>
        <span className={styles['centered-cell']}>{t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_RATING)}</span>
        <span className={styles['centered-cell']}>{t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_IN_BOUNDARY)}</span>
      </div>

      <section className={styles['table']}>
        {filterSchoolsByLevel(schools, schoolLevelSelected).map((school: School, index) => (
          <div className={styles['school-row']} key={index}>
            <div className={styles['name-column']}>
              <Link href={`/school/${school.slug}`} className={styles['school-row-name']} onClick={onSchoolLinkClick}>
                {school.name}
              </Link>
              <div className={styles['school-row-details']}>
                ({school.grades})&nbsp;
                {position && t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_KM_AWAY, { distance: distanceToMappable(school.position, position) })}
              </div>
            </div>
            <div className={styles['school-row-rating']}>
              {school.eqaoRating &&
                <div><span>{(school.eqaoRating)?.toFixed(1)}</span>/4</div>
              }
              {!school.eqaoRating && <span>-</span>}
            </div>
            <div className={`${styles['centered-cell']} ${styles['boundary-status']}`}>
              {school.boundary
                ? <>{doesSchoolBoundaryIncludePoint(school, position) ?
                  <CheckmarkCircleIcon className={styles['icon-check-circle']} /> :
                  <CloseCircleIcon className={styles['icon-x-circle']} />}</>
                : <span>-</span>
              }
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

/** Component to display nearby schools for a given position */
export default function NearbySchools({ position, schools: initialSchools, hasFetched, setHasFetched, setHasSchools, onSchoolLinkClick }: NearbySchoolsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const [schools, setSchools] = useState<School[]>(!initialSchools ? [] : initialSchools.map(school => new School(school)).sort((a, b) => schoolSortBasedOnBoundaryAndDistance(a, b, position)));

  const fetchNearbySchools = async () => {
    if (hasFetched || (schools && schools.length)) {
      return new Promise<School[]>(resolve => resolve(schools));
    } else {
      if (!position) {
        return null;
      }
      const params = { point: JSON.stringify(position) };
      return getNearbySchools(params).then(schools => {
        const sortedSchools = schools.map(school => new School(school)).sort((a, b) => schoolSortBasedOnBoundaryAndDistance(a, b, position));
        setHasFetched(true);
        setHasSchools(!!sortedSchools.length);
        setSchools(sortedSchools);
        return sortedSchools;
      });
    }
  };

  return (!initialSchools?.length ?
    <LoadingData promise={fetchNearbySchools()} emptyState={t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_NO_SCHOOLS_NEARBY)}>
      {data => <Schools schools={data} position={position} onSchoolLinkClick={onSchoolLinkClick} />}
    </LoadingData>
    : <Schools schools={schools} position={position} onSchoolLinkClick={onSchoolLinkClick} />);
}
//#endregion

//#region Helper Functions

/**
 * Calculate the distance between two points in kilometers
 *
 * @param point1 The first point
 * @param point2 The second point
 * @returns The distance between the two points in kilometers
 */
const distanceToMappable = (point1: Point, point2: Point) => {
  const distanceInMeters = getDistance({ lng: point1.longitude, lat: point1.latitude }, { lng: point2.longitude, lat: point2.latitude });
  return Math.round(distanceInMeters / 100) / 10;
};

/**
 * Sort schools based on boundary and distance
 *
 * @param school1 The first school
 * @param school2 The second school
 * @param point The point to check if the school boundary includes
 * @returns The sorted schools
 */
const schoolSortBasedOnBoundaryAndDistance = (school1: School, school2: School, point: Point) => {
  const school1InBoundary = school1.boundaryIncludesPoint({ lng: point.longitude, lat: point.latitude });
  const school2InBoundary = school2.boundaryIncludesPoint({ lng: point.longitude, lat: point.latitude });
  if (school1InBoundary && school2InBoundary) {
    return distanceToMappable(school1.position, point) > distanceToMappable(school2.position, point) ? 1 : -1;
  } else {
    return school2InBoundary ? 1 : -1;
  }
};

/** Filter schools by level */
const filterSchoolsByLevel = (schools: School[], level: SchoolLevel) => schools.filter(school => school.level === level);

/** Get the button class for the given school level */
const getButtonClass = (schoolLevelSelected: SchoolLevel, type: SchoolLevel): string => schoolLevelSelected === type ? styles.active : '';

/** Check if the school boundary includes the given point */
const doesSchoolBoundaryIncludePoint = (school: School, position: Point) => {
  if (position) {
    return school.boundaryIncludesPoint({ lng: position.longitude, lat: position.latitude });
  }
};

/**
 * Get the separator to be used when joining the school names.
 *
 * @param length The length of the list of schools
 * @param index The index of the school in the list
 * @returns The separator to be used when joining the school names
 */
const getSchoolNameListSeparator = (length: number, index: number, t: TFunction) => {
  if (index === length - 1) {
    return t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_SEPARATOR_PERIOD);
  } else {
    return length === 2 ?
      t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_SEPARATOR_OR) :
      t(LOCALE_COMMON_PATHS.ADDRESS_NEARBY_SCHOOLS_SEPARATOR_COMMA);
  }
};

/**
 * Filter schools by boundary
 *
 * @param schools The schools to filter
 * @param position The position to check if the school boundary includes
 * @returns The schools that are within the boundary
 */
const filterSchoolsByBoundary = (schools: School[], position: Point) => {
  return schools.reduce((schoolsWithinBoundary: School[], school) => {
    if (doesSchoolBoundaryIncludePoint(school, position)) {
      schoolsWithinBoundary.push(school);
    }
    return schoolsWithinBoundary;
  }, []);
};

//#endregion